/**
 * RTK Query slice for the SST V2 live-trace admin endpoints.
 *
 * Spec: dev/projects/sl2-sstv2-live-trace.md §1.5, §4.4
 *
 * Two endpoints:
 *   - getTraceList   → GET /proxy/sirsteward-v2?resourceType=traces&...
 *   - getTraceById   → GET /proxy/sirsteward-v2?resourceType=trace&resourceId=<id>
 *
 * Both are super-admin gated server-side via the fnThingsV2 `/proxy/sirsteward-v2`
 * `enforceSuperAdminRole(ctx)` call.
 */
import { createApi } from "@reduxjs/toolkit/query/react"
import { baseQuery } from "../baseQuerySl2"
import type {
  TraceListFilter,
  TraceListResult,
  TraceMetricDimension,
  TraceMetricsResult,
  TraceWithSteps,
} from "./types"

export type GetTraceListArgs = TraceListFilter & {
  limit?: number
  cursor?: string
}

export const sstv2TraceApi = createApi({
  reducerPath: "sstv2TraceApi",
  baseQuery,
  endpoints: (builder) => ({
    getTraceList: builder.query<{ data: TraceListResult }, GetTraceListArgs>({
      query: (args) => ({
        url: "proxy/sirsteward-v2",
        params: {
          resourceType: "traces",
          ...(args.tenantId ? { tenantId: args.tenantId } : {}),
          ...(args.eventType ? { eventType: args.eventType } : {}),
          ...(args.reservationId
            ? { reservationId: args.reservationId }
            : {}),
          ...(args.migrationId ? { migrationId: args.migrationId } : {}),
          ...(args.limit ? { limit: args.limit } : {}),
          ...(args.cursor ? { cursor: args.cursor } : {}),
        },
      }),
    }),
    getTraceById: builder.query<{ data: TraceWithSteps }, { traceId: string }>(
      {
        query: ({ traceId }) => ({
          url: "proxy/sirsteward-v2",
          params: {
            resourceType: "trace",
            resourceId: traceId,
          },
        }),
      },
    ),
    getTraceMetrics: builder.query<
      { data: TraceMetricsResult },
      { dimension: TraceMetricDimension; limit?: number }
    >({
      query: ({ dimension, limit }) => ({
        url: "proxy/sirsteward-v2",
        params: {
          resourceType: "traceMetrics",
          dimension,
          ...(limit ? { limit } : {}),
        },
      }),
    }),
  }),
})

export const {
  useGetTraceListQuery,
  useGetTraceByIdQuery,
  useGetTraceMetricsQuery,
} = sstv2TraceApi
